import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Ban, FileSignature, UserMinus } from 'lucide-react';
import { AddButton, Drawer, Field, QueryState, Select, StatusPill, formatDate, todayIso, useFormErrors, type Column } from '@/components/live';
import { DataGrid } from '@/components/DataGrid';
import { StatCards, applyStat } from '@/components/StatCards';
import { useRows } from '@/hooks/useResource';
import { callRpc, listRows } from '@/api/resources';
import { farmerOptions, plotOptions } from '@/api/lookups';
import { useAuth } from '@/auth/AuthProvider';
import { can } from '@/auth/session';
import { isSupabaseConfigured } from '@/lib/supabase';
import { toast } from '@/lib/toast';
import { LANGUAGES, languageOption } from '@/lib/languages';
import type { ConsentRow } from '@/types/records';

interface WithdrawalRow {
  id: string;
  withdrawal_code: string | null;
  farmer_id: string;
  farmer_name: string;
  farmer_code: string;
  village_name: string | null;
  scope: 'membership' | 'plots';
  plot_codes: string | null;
  plot_count: number;
  reason_code: string;
  reason_label: string | null;
  reason_text: string | null;
  requested_on: string;
  effective_date: string;
  consent_id: string | null;
  consent_status: string | null;
  consent_mode: string | null;
  withdrawal_status: 'pending_consent' | 'effective' | 'cancelled';
  effective_at: string | null;
  cancelled_reason: string | null;
}

export const WITHDRAWAL_REASONS = [
  { id: 'land_sold', label: 'Land sold' },
  { id: 'lease_ended', label: 'Lease ended' },
  { id: 'land_use_changed', label: 'Land use changed' },
  { id: 'not_interested', label: 'No longer interested' },
  { id: 'price_or_payment', label: 'Price or payment concerns' },
  { id: 'relocated', label: 'Moved away' },
  { id: 'deceased', label: 'Farmer deceased' },
  { id: 'dispute', label: 'Land or family dispute' },
  { id: 'compliance', label: 'Cannot meet the requirements' },
  { id: 'other', label: 'Other (say what)' },
];

const STATUS_LABEL: Record<WithdrawalRow['withdrawal_status'], string> = {
  pending_consent: 'Waiting on consent',
  effective: 'Effective',
  cancelled: 'Cancelled',
};

/**
 * The withdrawal register (owner, 2026-09-21; migration 0066). A farmer withdraws their whole
 * membership, or one or more plots, for a stated reason. Recording it issues a withdrawal consent
 * in their language; once they sign it - by OTP or on paper - the plots (or the farmer and all
 * their plots) become Withdrawn, with the reason on the record.
 */
export function WithdrawalRegister({ onOpenConsent }: { onOpenConsent: (consent: ConsentRow) => void }) {
  const { profile } = useAuth();
  const queryClient = useQueryClient();
  const [stat, setStat] = useState<string | null>(null);
  const [recording, setRecording] = useState(false);
  const [cancelling, setCancelling] = useState<WithdrawalRow | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const mayRecord = can(profile, 'party.consent', 'create');
  const mayCancel = can(profile, 'party.consent', 'update');

  const rows = useRows<WithdrawalRow>('withdrawals', { orderBy: { column: 'created_at', ascending: false } });

  const STATS = useMemo(() => [
    { key: 'all', label: 'Withdrawals', match: () => true, tone: 'neutral' as const },
    { key: 'pending', label: 'Waiting on consent', tone: 'warn' as const, match: (row: WithdrawalRow) => row.withdrawal_status === 'pending_consent' },
    { key: 'effective', label: 'Effective', tone: 'ok' as const, match: (row: WithdrawalRow) => row.withdrawal_status === 'effective' },
    { key: 'membership', label: 'Whole membership', tone: 'info' as const, match: (row: WithdrawalRow) => row.scope === 'membership' },
    { key: 'plots', label: 'Plots only', tone: 'info' as const, match: (row: WithdrawalRow) => row.scope === 'plots' },
    { key: 'cancelled', label: 'Cancelled', tone: 'error' as const, match: (row: WithdrawalRow) => row.withdrawal_status === 'cancelled' },
  ], []);
  const visible = useMemo(() => applyStat(rows.data ?? [], STATS, stat), [rows.data, STATS, stat]);

  async function openConsent(row: WithdrawalRow) {
    if (!row.consent_id) return;
    const consent = (await listRows<ConsentRow>('party_consents', { filters: { id: row.consent_id } }))[0];
    if (consent) onOpenConsent(consent); else toast.error('The withdrawal consent could not be opened.');
  }

  async function cancel() {
    if (!cancelling) return;
    if (!cancelReason.trim()) { toast.error('Say why it is cancelled.'); return; }
    try {
      await callRpc('fn_cancel_withdrawal', { p_withdrawal_id: cancelling.id, p_reason: cancelReason.trim() });
      toast.success('Withdrawal cancelled; its unsigned consent is revoked.');
      setCancelling(null); setCancelReason('');
      void queryClient.invalidateQueries({ queryKey: ['withdrawals'] });
      void queryClient.invalidateQueries({ queryKey: ['party_consents'] });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'It could not be cancelled.');
    }
  }

  const columns: Array<Column<WithdrawalRow>> = [
    { key: 'withdrawal_code', label: 'Ref', render: (row) => <strong>{row.withdrawal_code ?? '--'}</strong> },
    { key: 'farmer_name', label: 'Farmer', render: (row) => (
      <span className="cell-stack"><strong>{row.farmer_name}</strong><small className="muted">{row.farmer_code}</small></span>
    ) },
    { key: 'village_name', label: 'Village', render: (row) => row.village_name ?? '--' },
    { key: 'scope', label: 'Withdraws', render: (row) => (row.scope === 'membership'
      ? <span><UserMinus size={13} /> Whole membership</span> : `${row.plot_count} plot${row.plot_count === 1 ? '' : 's'}`) },
    { key: 'plot_codes', label: 'Plots', render: (row) => row.plot_codes ?? '--' },
    { key: 'reason_label', label: 'Reason', render: (row) => (
      <span className="cell-stack"><span>{row.reason_label}</span>{row.reason_text && <small className="muted">{row.reason_text}</small>}</span>
    ) },
    { key: 'effective_date', label: 'From', render: (row) => formatDate(row.effective_date) },
    { key: 'consent_status', label: 'Consent', render: (row) => <StatusPill value={row.consent_status} /> },
    { key: 'withdrawal_status', label: 'Status', render: (row) => <span className={`wd-status ${row.withdrawal_status}`}>{STATUS_LABEL[row.withdrawal_status]}</span> },
    { key: 'act', label: '', render: (row) => (
      <span className="row-actions" onClick={(event) => event.stopPropagation()}>
        {row.consent_id && (
          <button onClick={() => void openConsent(row)} type="button">
            <FileSignature size={14} /> {row.withdrawal_status === 'pending_consent' ? 'Get consent' : 'Consent'}
          </button>
        )}
        {row.withdrawal_status === 'pending_consent' && mayCancel && (
          <button className="cancel" onClick={() => setCancelling(row)} type="button"><Ban size={14} /> Cancel</button>
        )}
      </span>
    ) },
  ];

  return (
    <>
      <div className="toolbar-row">
        <AddButton disabled={!mayRecord} label="Record withdrawal" onClick={() => setRecording(true)}
                   title={mayRecord ? undefined : 'Your role cannot record a withdrawal.'} />
      </div>
      <StatCards active={stat} onSelect={setStat} rows={rows.data ?? []} stats={STATS} />
      <QueryState empty="No withdrawals recorded." query={rows}>
        <DataGrid columns={columns} exportName="withdrawals" onRowClick={(row) => void openConsent(row)} rows={visible}
                  screenKey="party.consent" searchPlaceholder="Farmer, code, village, plot" />
      </QueryState>

      {recording && (
        <RecordWithdrawalDrawer onClose={() => setRecording(false)} onDone={async (consentId) => {
          setRecording(false);
          await queryClient.invalidateQueries({ queryKey: ['withdrawals'] });
          await queryClient.invalidateQueries({ queryKey: ['party_consents'] });
          const consent = (await listRows<ConsentRow>('party_consents', { filters: { id: consentId } }))[0];
          if (consent) onOpenConsent(consent);
        }} />
      )}

      <Drawer
        footer={
          <>
            <button onClick={() => setCancelling(null)} type="button">Keep it</button>
            <button className="danger" onClick={() => void cancel()} type="button">Cancel withdrawal</button>
          </>
        }
        onClose={() => setCancelling(null)}
        open={Boolean(cancelling)}
        subtitle={cancelling ? `${cancelling.withdrawal_code} - ${cancelling.farmer_name}` : undefined}
        title="Cancel this withdrawal?"
      >
        <p className="muted" style={{ margin: 0 }}>The farmer stays enrolled, and the unsigned withdrawal consent is revoked.</p>
        <Field label="Why is it cancelled?" required>
          <input onChange={(event) => setCancelReason(event.target.value)} placeholder="e.g. The farmer changed their mind" value={cancelReason} />
        </Field>
      </Drawer>
    </>
  );
}

function RecordWithdrawalDrawer({ onClose, onDone }: { onClose: () => void; onDone: (consentId: string) => void }) {
  const { errors, check } = useFormErrors();
  const [form, setForm] = useState({
    farmer_id: '', scope: 'plots' as 'plots' | 'membership', plot_ids: [] as string[],
    reason_code: '', reason_text: '', effective_date: todayIso(), language_code: 'en',
  });
  const [saving, setSaving] = useState(false);

  const farmers = useQuery({ queryKey: ['picker', 'farmers'], queryFn: () => farmerOptions(), enabled: isSupabaseConfigured });
  const plots = useQuery({
    queryKey: ['picker', 'plots', form.farmer_id], queryFn: () => plotOptions(form.farmer_id),
    enabled: isSupabaseConfigured && Boolean(form.farmer_id),
  });
  const openPlots = (plots.data ?? []).filter((plot) => plot.status !== 'withdrawn');

  function togglePlot(id: string) {
    setForm((now) => ({ ...now, plot_ids: now.plot_ids.includes(id) ? now.plot_ids.filter((entry) => entry !== id) : [...now.plot_ids, id] }));
  }

  async function save() {
    const ok = check({
      farmer_id: form.farmer_id ? undefined : 'Which farmer is withdrawing?',
      plot_ids: form.scope === 'plots' && form.plot_ids.length === 0 ? 'Tick the plots being withdrawn.' : undefined,
      reason_code: form.reason_code ? undefined : 'Why is the farmer withdrawing?',
      reason_text: form.reason_code === 'other' && !form.reason_text.trim() ? 'Say what the reason is.' : undefined,
    });
    if (!ok) return;
    setSaving(true);
    try {
      const id = await callRpc<string>('fn_request_withdrawal', {
        p_farmer_id: form.farmer_id, p_scope: form.scope, p_plot_ids: form.scope === 'plots' ? form.plot_ids : null,
        p_reason_code: form.reason_code, p_reason_text: form.reason_text.trim() || null,
        p_effective_date: form.effective_date, p_language_code: form.language_code,
      });
      const row = (await listRows<{ consent_id: string }>('withdrawals', { filters: { id } }))[0];
      toast.success('Withdrawal recorded. Now take the farmer\'s consent to it.');
      if (row?.consent_id) onDone(row.consent_id); else onClose();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'The withdrawal could not be recorded.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Drawer
      footer={
        <>
          <button className="cancel" onClick={onClose} type="button">Cancel</button>
          <button className="primary" disabled={saving} onClick={() => void save()} type="button">
            {saving ? 'Recording...' : 'Record and take consent'}
          </button>
        </>
      }
      onClose={onClose}
      open
      subtitle="It takes effect when the farmer signs the withdrawal consent - by OTP or on paper."
      title="Record a withdrawal"
    >
      <Field error={errors.farmer_id} label="Farmer" required>
        <Select onChange={(value) => setForm({ ...form, farmer_id: value, plot_ids: [] })} options={farmers.data ?? []}
                placeholder={farmers.isLoading ? 'Loading...' : 'Select farmer'} value={form.farmer_id} />
      </Field>
      <Field label="What is being withdrawn">
        <div aria-label="Scope" className="view-toggle member-type" role="group">
          <button aria-pressed={form.scope === 'plots'} onClick={() => setForm({ ...form, scope: 'plots' })} type="button">One or more plots</button>
          <button aria-pressed={form.scope === 'membership'} onClick={() => setForm({ ...form, scope: 'membership', plot_ids: [] })} type="button">
            Whole membership (all plots)
          </button>
        </div>
      </Field>
      {form.scope === 'plots' && (
        <Field error={errors.plot_ids} label="Plots" required>
          {!form.farmer_id ? <p className="muted" style={{ margin: 0 }}>Choose the farmer first.</p>
            : openPlots.length === 0 ? <p className="muted" style={{ margin: 0 }}>{plots.isLoading ? 'Loading...' : 'This farmer has no enrolled plots.'}</p>
              : (
                <div className="check-list">
                  {openPlots.map((plot) => (
                    <label key={plot.id}>
                      <input checked={form.plot_ids.includes(plot.id)} onChange={() => togglePlot(plot.id)} type="checkbox" />
                      {plot.label}
                    </label>
                  ))}
                </div>
              )}
        </Field>
      )}
      <div className="field-row">
        <Field error={errors.reason_code} label="Reason" required>
          <Select onChange={(value) => setForm({ ...form, reason_code: value })} options={WITHDRAWAL_REASONS}
                  placeholder="Select reason" value={form.reason_code} />
        </Field>
        <Field label="Takes effect from">
          <input onChange={(event) => setForm({ ...form, effective_date: event.target.value })} type="date" value={form.effective_date} />
        </Field>
      </div>
      <Field error={errors.reason_text} hint={form.reason_code === 'other' ? 'Required for "Other".' : 'In the farmer\'s words, if they gave any.'}
             label="Reason in words" required={form.reason_code === 'other'}>
        <textarea onChange={(event) => setForm({ ...form, reason_text: event.target.value })} rows={2} value={form.reason_text} />
      </Field>
      <Field hint="The withdrawal consent is issued in this language." label="Language">
        <Select onChange={(value) => setForm({ ...form, language_code: value })}
                options={LANGUAGES.map((language) => languageOption(language.code))} value={form.language_code} />
      </Field>
    </Drawer>
  );
}
